import React from 'react';

import { LabeledValue } from '@trqk/components';
import { hasGapAnalysisData, locale } from '@trqk/utils';

import { dataSourceLocaleStringOptions } from '../constants';
import { getGapDataSourceLabels } from '../utils';

interface DataSourcesProps {
    /**
     * The current context of the Create Gap Analysis state machine.
     */
    context: GapAnalysisContext;
}

/**
 * Shows {@link LabeledValue } components for performances and detections. Each <LabeledValue> is
 * only shown if the `context.selectedPeriod` actually has the relevant data. If the
 * `context.selectedPeriod` has neither performance or detection data, returns null.
 * @param props - {@link DataSourcesProps}
 */
export const DataSources = ({
    context,
}: DataSourcesProps): JSX.Element | null => {
    const { selectedPeriod, trqkGap } = context;

    const { hasDetectionData, hasPerformanceData } = hasGapAnalysisData(
        selectedPeriod,
        trqkGap,
    );

    const sourceLabels = getGapDataSourceLabels({
        selectedPeriod,
        localeStringOptions: dataSourceLocaleStringOptions,
        trqkGap,
    });

    const performanceValue = sourceLabels.performances.dateRange ?? null;

    const detectionValue =
        sourceLabels.detections.dateRange ??
        sourceLabels.detections.filename ??
        null;

    if (!hasDetectionData || !hasPerformanceData) {
        return null;
    }

    return (
        <div className="flex space-x-6 mt-1">
            {hasPerformanceData && performanceValue !== null && (
                <LabeledValue
                    label={
                        locale.enUS.MANAGE_GAP_ANALYSIS.DATA_LABELS.PERFORMANCES
                    }
                    value={performanceValue}
                    maxWidth={200}
                />
            )}
            {hasDetectionData && detectionValue !== null && (
                <LabeledValue
                    label={
                        locale.enUS.MANAGE_GAP_ANALYSIS.DATA_LABELS.DETECTIONS
                    }
                    value={detectionValue}
                    maxWidth={200}
                />
            )}
        </div>
    );
};
